/**
 * useSellerGoals - Hook para gerenciamento de metas de vendedores CRM
 * 
 * Fornece operações CRUD completas para metas de vendedores,
 * utilizando RPCs seguras com arquitetura tenant v2.0.
 * 
 * @version 1.0.0
 * @author Sistema
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

// ============================================================================
// INTERFACES - Tipos para metas de vendedores
// ============================================================================

/**
 * Interface de meta retornada pela RPC list_seller_goals
 * Inclui dados do vendedor, workspaces associados e progresso calculado
 */
export interface SellerGoal {
  id: string;
  seller_id: string;
  seller_name: string;
  seller_email: string | null;
  seller_avatar_url: string | null;
  goal_type: 'revenue' | 'deals_count';
  target_value: number;
  period_start: string;
  period_end: string;
  notes: string | null;
  achieved_value: number;
  achievement_percentage: number;
  /** IDs dos workspaces associados à meta */
  workspace_ids: string[];
  /** Nomes dos workspaces associados à meta */
  workspace_names: string[];
  created_at: string;
  updated_at: string;
}

/**
 * Input para criação de nova meta
 */
export interface CreateGoalInput {
  seller_id: string;
  goal_type: 'revenue' | 'deals_count';
  target_value: number;
  period_start: string;
  period_end: string;
  notes?: string | null;
  /** IDs dos workspaces a associar à meta */
  workspace_ids?: string[];
}

/**
 * Input para atualização de meta existente
 */
export interface UpdateGoalInput {
  id: string;
  target_value?: number;
  period_start?: string;
  period_end?: string;
  notes?: string | null;
  /** IDs dos workspaces a associar à meta */
  workspace_ids?: string[];
}

/**
 * Filtros para listagem de metas
 */
export interface GoalFilters {
  seller_id?: string | null;
  goal_type?: 'revenue' | 'deals_count' | null;
  period_start?: string | null;
  period_end?: string | null;
  /** Filtrar por workspace específico */
  workspace_id?: string | null;
}

// ============================================================================
// HOOK PRINCIPAL - useSellerGoals
// ============================================================================

/**
 * Hook para CRUD de metas de vendedores
 * 
 * @param filters - Filtros opcionais para listagem
 * @returns Objeto com dados, estados e mutations
 */
export function useSellerGoals(filters: GoalFilters = {}) {
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();

  // ============================================================================
  // QUERY - Lista de metas com filtros
  // ============================================================================

  const {
    data: goals = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['seller-goals', currentTenant?.id, filters],
    queryFn: async () => {
      if (!currentTenant?.id) {
        logger.debug('[useSellerGoals]', 'Sem tenant ativo, retornando array vazio');
        return [];
      }

      logger.debug('[useSellerGoals]', 'Buscando metas com filtros:', JSON.stringify(filters));

      const { data, error } = await supabase.rpc('list_seller_goals', {
        p_seller_id: filters.seller_id || null,
        p_goal_type: filters.goal_type || null,
        p_period_start: filters.period_start || null,
        p_period_end: filters.period_end || null,
        p_workspace_id: filters.workspace_id || null,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('[useSellerGoals]', 'Erro ao buscar metas:', error.message);
        throw error;
      }

      logger.debug('[useSellerGoals]', 'Metas carregadas:', String(data?.length || 0));
      return (data || []) as SellerGoal[];
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000, // 30 segundos
  });

  // ============================================================================
  // MUTATION - Criar meta
  // ============================================================================

  const createGoal = useMutation({
    mutationFn: async (input: CreateGoalInput) => {
      if (!currentTenant?.id) {
        throw new Error('Tenant não encontrado');
      }

      logger.debug('[useSellerGoals]', 'Criando meta:', JSON.stringify(input));

      const { data, error } = await supabase.rpc('create_seller_goal', {
        p_seller_id: input.seller_id,
        p_goal_type: input.goal_type,
        p_target_value: input.target_value,
        p_period_start: input.period_start,
        p_period_end: input.period_end,
        p_notes: input.notes || null,
        p_workspace_ids: input.workspace_ids || null,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('[useSellerGoals]', 'Erro ao criar meta:', error.message);
        throw error;
      }

      logger.debug('[useSellerGoals]', 'Meta criada com ID:', String(data));
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seller-goals'] });
      queryClient.invalidateQueries({ queryKey: ['my-goals-progress'] });
      queryClient.invalidateQueries({ queryKey: ['my-seller-performance'] });
      queryClient.invalidateQueries({ queryKey: ['my-monthly-evolution'] });
      queryClient.invalidateQueries({ queryKey: ['overview-kpis'] });
      toast.success('Meta criada com sucesso!');
    },
    onError: (error: Error) => {
      logger.error('[useSellerGoals]', 'Falha ao criar meta:', error.message);
      toast.error(error.message || 'Erro ao criar meta');
    },
  });

  // ============================================================================
  // MUTATION - Atualizar meta
  // ============================================================================

  const updateGoal = useMutation({
    mutationFn: async (input: UpdateGoalInput) => {
      if (!currentTenant?.id) {
        throw new Error('Tenant não encontrado');
      }

      logger.debug('[useSellerGoals]', 'Atualizando meta:', JSON.stringify(input));

      const { data, error } = await supabase.rpc('update_seller_goal', {
        p_goal_id: input.id,
        p_target_value: input.target_value ?? null,
        p_period_start: input.period_start ?? null,
        p_period_end: input.period_end ?? null,
        p_notes: input.notes ?? null,
        p_workspace_ids: input.workspace_ids ?? null,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('[useSellerGoals]', 'Erro ao atualizar meta:', error.message);
        throw error;
      }

      logger.debug('[useSellerGoals]', 'Meta atualizada:', String(data));
      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seller-goals'] });
      queryClient.invalidateQueries({ queryKey: ['my-goals-progress'] });
      queryClient.invalidateQueries({ queryKey: ['my-seller-performance'] });
      queryClient.invalidateQueries({ queryKey: ['my-monthly-evolution'] });
      queryClient.invalidateQueries({ queryKey: ['overview-kpis'] });
      toast.success('Meta atualizada com sucesso!');
    },
    onError: (error: Error) => {
      logger.error('[useSellerGoals]', 'Falha ao atualizar meta:', error.message);
      toast.error(error.message || 'Erro ao atualizar meta');
    },
  });

  // ============================================================================
  // MUTATION - Deletar meta
  // ============================================================================

  const deleteGoal = useMutation({
    mutationFn: async (goalId: string) => {
      if (!currentTenant?.id) {
        throw new Error('Tenant não encontrado');
      }

      logger.debug('[useSellerGoals]', 'Deletando meta:', goalId);

      const { data, error } = await supabase.rpc('delete_seller_goal', {
        p_goal_id: goalId,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('[useSellerGoals]', 'Erro ao deletar meta:', error.message);
        throw error;
      }

      logger.debug('[useSellerGoals]', 'Meta deletada:', String(data));
      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seller-goals'] });
      queryClient.invalidateQueries({ queryKey: ['my-goals-progress'] });
      queryClient.invalidateQueries({ queryKey: ['my-seller-performance'] });
      queryClient.invalidateQueries({ queryKey: ['my-monthly-evolution'] });
      queryClient.invalidateQueries({ queryKey: ['overview-kpis'] });
      toast.success('Meta excluída com sucesso!');
    },
    onError: (error: Error) => {
      logger.error('[useSellerGoals]', 'Falha ao deletar meta:', error.message);
      toast.error(error.message || 'Erro ao excluir meta');
    },
  });

  // ============================================================================
  // RETURN - Exposição da API do hook
  // ============================================================================

  return {
    // Dados
    goals,
    
    // Estados
    isLoading,
    error,
    
    // Ações
    refetch,
    
    // Mutations
    createGoal,
    updateGoal,
    deleteGoal,
  };
}
